import React, { useEffect } from "react";
import "./FasoorModal.css";

// =====================================================
// TYPES
// =====================================================

export type FasoorModalType =
  | "warning"
  | "danger"
  | "success"
  | "info"
  | "game";

interface FasoorModalProps {
  open: boolean;

  type?: FasoorModalType;

  title: string;

  message: string;

  description?: string;

  confirmText?: string;

  cancelText?: string;

  showCancel?: boolean;

  closeOnOverlay?: boolean;

  onConfirm?: () => void;

  onCancel?: () => void;
}

// =====================================================
// ICONS
// =====================================================

const icons: Record<
  FasoorModalType,
  string
> = {
  warning: "⚠",
  danger: "!",
  success: "✓",
  info: "i",
  game: "♠",
};

// =====================================================
// COMPONENT
// =====================================================

const FasoorModal: React.FC<
  FasoorModalProps
> = ({
  open,

  type = "info",

  title,

  message,

  description,

  confirmText = "OK",

  cancelText = "Cancel",

  showCancel = false,

  closeOnOverlay = false,

  onConfirm,

  onCancel,
}) => {

  // ===================================================
  // ESC KEY
  // ===================================================

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (
      event: KeyboardEvent
    ) => {
      if (event.key === "Escape") {
        onCancel?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [open, onCancel]);

  // ===================================================
  // NOT OPEN
  // ===================================================

  if (!open) {
    return null;
  }

  // ===================================================
  // RETURN
  // ===================================================

  return (
    <div
      className="fasoor-modal-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (
          closeOnOverlay &&
          event.target ===
            event.currentTarget
        ) {
          onCancel?.();
        }
      }}
    >
      <div
        className={`fasoor-modal fasoor-modal-${type}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fasoor-modal-title"
      >

        {/* ICON */}

        <div className="fasoor-modal-icon">
          {icons[type]}
        </div>

        {/* TITLE */}

        <h2 id="fasoor-modal-title">
          {title}
        </h2>

        {/* MESSAGE */}

        <p className="fasoor-modal-message">
          {message}
        </p>

        {/* OPTIONAL DESCRIPTION */}

        {description && (
          <p className="fasoor-modal-description">
            {description}
          </p>
        )}

        {/* ACTIONS */}

        <div className="fasoor-modal-actions">

          {showCancel && (
            <button
              type="button"
              className="fasoor-modal-cancel"
              onClick={onCancel}
            >
              {cancelText}
            </button>
          )}

          <button
            type="button"
            className={`fasoor-modal-confirm fasoor-modal-confirm-${type}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>

        </div>

      </div>
    </div>
  );
};

export default FasoorModal;